import React, { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEmojiSearch } from "./useEmojiSearch";
import { CATEGORY_LABELS, type EmojiItem } from "./emoji-data";
import { Search } from "lucide-react";

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect }) => {
  const [query, setQuery] = useState("");
  const emojis = useEmojiSearch(query);

  const grouped = emojis.reduce<Record<string, EmojiItem[]>>((acc, item) => {
    (acc[item.category] ??= []).push(item);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-2">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search emoji…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-md border border-input bg-background pl-8 pr-3 h-8 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      {/* Grid */}
      <ScrollArea className="max-h-[200px]">
        <div className="flex flex-col gap-2 pr-2">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                {CATEGORY_LABELS[category as EmojiItem["category"]] ?? category}
              </p>
              <div className="grid grid-cols-7 gap-0.5">
                {items.map((item) => (
                  <button
                    key={item.emoji}
                    type="button"
                    onClick={() => onSelect(item.emoji)}
                    className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors text-lg cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label={item.name}
                  >
                    {item.emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {emojis.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No results found.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
